// src/api/shifts.js
import { supabase } from './supabaseClient';

/**
 * Manages the single global shift state.
 * @param {Object} options - Options for the shift operation
 * @param {string} [options.operation] - 'start', 'stop', or 'check' (default: 'check')
 * @returns {Promise<Object>} Shift data or status
 * @throws {Error} If the operation fails
 */
export async function manageShift({ operation = 'check' } = {}) {
  try {
    // No user check needed since it’s a single global shift
    if (operation === 'start') {
      // Check current state
      const { data: currentShift, error: checkError } = await supabase
        .from('shifts')
        .select('*')
        .eq('id', 1)
        .single();

      if (checkError) throw new Error(`Failed to check shift: ${checkError.message}`);
      if (currentShift.is_active) {
        return { message: 'Shift already active', shift: currentShift };
      }

      // Start the shift (set is_active to true)
      const { data, error } = await supabase
        .from('shifts')
        .update({ is_active: true, created_at: new Date().toISOString(), updated_at: new Date().toISOString() })
        .eq('id', 1)
        .select()
        .single();

      if (error) throw new Error(`Failed to start shift: ${error.message}`);
      return { message: 'Shift started', shift: data };
    }

    if (operation === 'stop') {
      // Check current state
      const { data: currentShift, error: checkError } = await supabase
        .from('shifts')
        .select('*')
        .eq('id', 1)
        .single();

      if (checkError) throw new Error(`Failed to check shift: ${checkError.message}`);
      if (!currentShift.is_active) {
        return { message: 'No active shift to stop', shift: currentShift };
      }

      // Stop the shift (set is_active to false)
      const { data, error } = await supabase
        .from('shifts')
        .update({ is_active: false, updated_at: new Date().toISOString() })
        .eq('id', 1)
        .select()
        .single();

      if (error) throw new Error(`Failed to stop shift: ${error.message}`);
      return { message: 'Shift stopped', shift: data };
    }

    if (operation === 'check') {
      // Check the current shift state
      const { data, error } = await supabase
        .from('shifts')
        .select('*')
        .eq('id', 1)
        .single();

      if (error) throw new Error(`Failed to check shift: ${error.message}`);
      if (data.is_active) {
        return { message: 'Shift is active', shift: data };
      }
      return { message: 'No active shift', shift: data };
    }

    throw new Error(`Invalid operation: ${operation}`);
  } catch (error) {
    console.error('manageShift error:', error.message);
    throw error;
  }
}